/* Song model for the "Şarkılar" tab: transcribe the chords bar by bar, then play along with the mic.
   See .claude/skills/add-song/SKILL.md for the authoring protocol. */
import type { LyricLine } from '../../core/jam/lyrics';
import type { ChordName, RootName } from '../../core/theory';

export type { LyricLine };

/** Key as its tonic chord: "G" (Sol majör) or "Am" (La minör). */
export type KeyName = RootName | `${RootName}m`;

export interface SongSection {
  /** Turkish section name: "Giriş", "Kıta", "Nakarat", "Köprü", "Son"... */
  name: string;
  /** One chord per bar, as it sounds (concert pitch). */
  bars: ChordName[];
}

export interface CoverInfo {
  /** Original artist, e.g. "Duman". */
  artist: string;
  /** Pages the chords were checked against: at least two independent sources. */
  references: string[];
  /** Capo fret of the usual guitar arrangement; bars still hold the sounding chords. */
  capo?: number;
  /** Optional official listening link (artist channel, label, streaming page). */
  listen?: string;
  year?: number;
}

export interface Song {
  /** Kebab-case, unique, stable: used in the URL (/akor/sarki/<id>) and as the progress key. */
  id: string;
  title: string;
  /** 1 = Kolay, 2 = Orta, 3 = Zor */
  level: 1 | 2 | 3;
  key: KeyName;
  bpm: number;
  /** Beats per bar. */
  beats: 2 | 3 | 4 | 6;
  /** Strum pattern, one character per eighth note: D = down, U = up, - = rest. Length = beats × 2. */
  pattern: string;
  /** Genre tag shown on the card (Turkish): "Pop", "Türk rock", "Vals"... */
  style: string;
  /** One or two Turkish sentences; inline markup allowed ({{pc:A}}, **bold**). */
  desc: string;
  sections: SongSection[];
  /** Chord buttons offered while transcribing. Default: the key's diatonic chords plus every chord in the song. */
  palette?: ChordName[];
  /** Present for real songs by other artists. Covers carry chords only: never lyrics or melody. */
  cover?: CoverInfo;
  /** Timed lyrics, shown karaoke-style in Jam. Only for the app's own songs: a cover never has lyrics.
      Each line starts on a bar (counted from 1) and gives every word its length in beats. */
  lyrics?: LyricLine[];
}

/** Identity helper that gives song files full type checking and autocompletion. */
export const defineSong = (song: Song): Song => song;
